import type { PreflightCheck } from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toDataURL } from "qrcode";
import { useEffect, useMemo, useState, type SyntheticEvent } from "react";
import { Navigate, useNavigate } from "react-router";

import { Button, CopyButton, PasswordField, TextField } from "../../components/ui/Controls";
import { AlertBanner, ErrorState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { RecoveryCodePanel } from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { setupApi, type TotpSetupStartResponse } from "../../lib/api";
import { usePageTitle } from "../../route-meta";

const checkLabels: Readonly<Record<PreflightCheck["code"], string>> = {
  database: "Yönetim veritabanı",
  encryption: "Şifreleme anahtarı",
  queue: "İş kuyruğu",
  agent: "Agent bağlantısı",
  caddy: "Caddy bağlantısı",
};

function CheckRow({ check }: { readonly check: PreflightCheck }) {
  const status =
    check.status === "ready" ? "healthy" : check.status === "checking" ? "checking" : "warning";
  const label =
    check.status === "ready"
      ? "Hazır"
      : check.status === "checking"
        ? "Kontrol ediliyor"
        : "Düzeltme gerekiyor";
  return (
    <li className="card stack">
      <div className="cluster" style={{ justifyContent: "space-between" }}>
        <strong>{checkLabels[check.code]}</strong>
        <StatusBadge label={label} status={status} />
      </div>
      <p>{check.diagnostic}</p>
      {check.remediation === undefined ? null : (
        <p className="muted">Güvenli sonraki adım: {check.remediation}</p>
      )}
    </li>
  );
}

export function SetupPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpSetupStartResponse>();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[]>();
  const [totpEnabled, setTotpEnabled] = useState(false);
  usePageTitle("Kurulum");

  const statusQuery = useQuery({
    queryKey: ["setup-status"],
    queryFn: ({ signal }) => setupApi.status(signal),
    retry: false,
  });
  const preflightQuery = useQuery({
    queryKey: ["setup-preflight"],
    queryFn: ({ signal }) => setupApi.preflight(signal),
    enabled: statusQuery.data?.state === "open",
    retry: false,
  });

  useEffect(() => {
    if (statusQuery.data?.state === "onboarding") setStep(3);
  }, [statusQuery.data?.state]);
  useEffect(() => {
    if (totpSetup === undefined) return;
    let active = true;
    void toDataURL(totpSetup.provisioningUri, { margin: 2, width: 240 }).then((url) => {
      if (active) setQrDataUrl(url);
    });
    return () => {
      active = false;
    };
  }, [totpSetup]);

  const passwordRules = useMemo(
    () => [
      "En az 12 karakter",
      "Daha önce kullanılmamış bir parola",
      "Adınızdan ve e-posta adresinizden farklı",
    ],
    [],
  );
  if (statusQuery.isLoading)
    return (
      <AuthFrame step={1}>
        <Skeleton label="Kurulum durumu yükleniyor" />
      </AuthFrame>
    );
  if (statusQuery.isError)
    return (
      <AuthFrame step={1}>
        <ErrorState
          onRetry={() => {
            void statusQuery.refetch();
          }}
        />
      </AuthFrame>
    );
  if (statusQuery.data?.state === "closed") {
    if (auth.loading)
      return (
        <AuthFrame step={1}>
          <Skeleton label="Oturum doğrulanıyor" />
        </AuthFrame>
      );
    return auth.identity === undefined ? (
      <Navigate replace state={{ setupComplete: true }} to="/giris" />
    ) : (
      <Navigate replace to="/genel-bakis" />
    );
  }

  const createOwner = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== passwordConfirmation) {
      setFormError("Parola doğrulaması eşleşmiyor.");
      return;
    }
    setBusy(true);
    setFormError(undefined);
    try {
      await setupApi.createOwner(
        { displayName, email, password, passwordConfirmation },
        bootstrapToken,
      );
      await queryClient.invalidateQueries({ queryKey: ["identity"] });
      await queryClient.invalidateQueries({ queryKey: ["setup-status"] });
      setStep(3);
    } catch (error) {
      setFormError(apiErrorMessage(error));
    } finally {
      setBootstrapToken("");
      setPassword("");
      setPasswordConfirmation("");
      setBusy(false);
    }
  };

  const startTotp = async () => {
    setBusy(true);
    setFormError(undefined);
    try {
      setTotpSetup(await setupApi.startTotp());
    } catch (error) {
      setFormError(apiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const confirmTotp = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (totpSetup === undefined) return;
    setBusy(true);
    setFormError(undefined);
    try {
      const result = await setupApi.confirmTotp({
        credentialId: totpSetup.credentialId,
        code: totpCode,
      });
      setRecoveryCodes(result.recoveryCodes);
      setTotpEnabled(true);
      setTotpSetup(undefined);
      setTotpCode("");
    } catch (error) {
      setFormError(apiErrorMessage(error));
      setTotpCode("");
    } finally {
      setBusy(false);
    }
  };
  const finish = async (mfaEnabled: boolean) => {
    setBusy(true);
    setFormError(undefined);
    try {
      const result = await setupApi.complete();
      setTotpEnabled(result.totpEnabled || mfaEnabled);
      setStep(4);
    } catch (error) {
      setFormError(apiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame step={step}>
      {step === 1 ? (
        <section className="stack">
          <div>
            <h1>Sistem kontrolü</h1>
            <p className="auth-copy">
              İlk owner hesabını oluşturmadan önce temel hizmetlerin güvenli biçimde hazır olduğunu
              doğrulayın.
            </p>
          </div>
          {preflightQuery.isLoading ? (
            <Skeleton />
          ) : preflightQuery.isError ? (
            <ErrorState
              onRetry={() => {
                void preflightQuery.refetch();
              }}
            />
          ) : (
            <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {preflightQuery.data?.checks.map((check) => (
                <CheckRow check={check} key={check.code} />
              ))}
            </ul>
          )}
          <Button
            disabled={preflightQuery.data === undefined}
            onClick={() => {
              if (preflightQuery.data?.canContinue === true) setStep(2);
              else void preflightQuery.refetch();
            }}
          >
            {preflightQuery.data?.canContinue === true
              ? "Owner hesabını oluştur"
              : "Kontrolleri yeniden çalıştır"}
          </Button>
        </section>
      ) : null}
      {step === 2 ? (
        <section className="stack">
          <div>
            <h1>Owner hesabı</h1>
            <p className="auth-copy">
              Kurulum anahtarı yalnızca bu işlem için kullanılır ve tarayıcıda saklanmaz.
            </p>
          </div>
          <form
            className="form-stack"
            onSubmit={(event) => {
              void createOwner(event);
            }}
          >
            <PasswordField
              autoComplete="off"
              help={
                <span>
                  Sunucuda{" "}
                  <code>sudo /usr/local/lib/webyonetim/operations-bootstrap-token --show</code>{" "}
                  komutuyla görüntülenen tek kullanımlık anahtarı girin.
                </span>
              }
              label="Kurulum anahtarı"
              onChange={(event) => {
                setBootstrapToken(event.currentTarget.value);
              }}
              required
              value={bootstrapToken}
            />
            <TextField
              autoComplete="name"
              label="Ad ve soyad"
              onChange={(event) => {
                setDisplayName(event.currentTarget.value);
              }}
              required
              value={displayName}
            />
            <TextField
              autoComplete="email"
              label="E-posta"
              onChange={(event) => {
                setEmail(event.currentTarget.value);
              }}
              required
              type="email"
              value={email}
            />
            <PasswordField
              autoComplete="new-password"
              help={
                <ul>
                  {passwordRules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              }
              label="Parola"
              minLength={12}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
              }}
              required
              value={password}
            />
            <PasswordField
              autoComplete="new-password"
              label="Parolayı doğrula"
              minLength={12}
              onChange={(event) => {
                setPasswordConfirmation(event.currentTarget.value);
              }}
              required
              value={passwordConfirmation}
            />
            {formError === undefined ? null : (
              <AlertBanner heading="Hesap oluşturulamadı" tone="error">
                <p>{formError}</p>
              </AlertBanner>
            )}
            <Button busy={busy} busyLabel="Owner hesabı oluşturuluyor…" type="submit">
              Owner hesabını oluştur
            </Button>
          </form>
        </section>
      ) : null}
      {step === 3 ? (
        <section className="stack">
          <div>
            <h1>İki adımlı doğrulama</h1>
            <p className="auth-copy">Hesabınızı parola ele geçirilmesine karşı koruyun.</p>
          </div>
          {recoveryCodes !== undefined ? (
            <RecoveryCodePanel
              codes={recoveryCodes}
              onClose={() => {
                setRecoveryCodes(undefined);
                void finish(true);
              }}
            />
          ) : null}
          {recoveryCodes === undefined && totpSetup === undefined ? (
            <>
              <AlertBanner heading="Önerilen güvenlik adımı" tone="warning">
                <p>
                  Bu adımı atlarsanız Genel Bakış ve Güvenlik sayfalarında kalıcı bir uyarı
                  görürsünüz.
                </p>
              </AlertBanner>
              <div className="form-actions">
                <Button
                  busy={busy}
                  busyLabel="Hazırlanıyor…"
                  onClick={() => {
                    void startTotp();
                  }}
                >
                  İki adımlı doğrulamayı etkinleştir
                </Button>
                <Button
                  disabled={busy}
                  onClick={() => {
                    void finish(false);
                  }}
                  variant="secondary"
                >
                  Şimdilik atla
                </Button>
              </div>
            </>
          ) : null}
          {totpSetup !== undefined ? (
            <form
              className="form-stack"
              onSubmit={(event) => {
                void confirmTotp(event);
              }}
            >
              {qrDataUrl === undefined ? (
                <Skeleton label="QR kodu hazırlanıyor" />
              ) : (
                <img
                  alt="Kimlik doğrulama uygulaması için QR kodu"
                  height="240"
                  src={qrDataUrl}
                  width="240"
                />
              )}
              <div className="card">
                <p>El ile kurulum anahtarı</p>
                <code>{totpSetup.manualKey}</code>
                <CopyButton label="Kurulum anahtarını kopyala" value={totpSetup.manualKey} />
              </div>
              <TextField
                autoComplete="one-time-code"
                inputMode="numeric"
                label="6 haneli doğrulama kodu"
                maxLength={6}
                onChange={(event) => {
                  setTotpCode(event.currentTarget.value);
                }}
                pattern="[0-9]{6}"
                required
                value={totpCode}
              />
              {formError === undefined ? null : (
                <AlertBanner heading="Kod doğrulanamadı" tone="error">
                  <p>{formError}</p>
                </AlertBanner>
              )}
              <Button busy={busy} busyLabel="Doğrulanıyor…" type="submit">
                İki adımlı doğrulamayı etkinleştir
              </Button>
            </form>
          ) : null}
        </section>
      ) : null}
      {step === 4 ? (
        <section className="stack">
          <h1>Kurulum tamamlandı</h1>
          <StatusBadge label="Platform hazır" status="healthy" />
          <dl>
            <dt>Owner e-postası</dt>
            <dd>{email || auth.identity?.user.email}</dd>
            <dt>İki adımlı doğrulama</dt>
            <dd>{totpEnabled ? "Etkin" : "Şimdilik etkin değil"}</dd>
          </dl>
          <Button
            onClick={() => {
              void navigate("/genel-bakis", { replace: true });
            }}
          >
            Panele git
          </Button>
        </section>
      ) : null}
    </AuthFrame>
  );
}

function AuthFrame({
  children,
  step,
}: {
  readonly children: React.ReactNode;
  readonly step: 1 | 2 | 3 | 4;
}) {
  return (
    <main className="auth-layout" id="setup-content" tabIndex={-1}>
      <a className="skip-link" href="#setup-content">
        Ana içeriğe geç
      </a>
      <div className="card setup-card stack">
        <div aria-label="Kurulum ilerlemesi" className="cluster">
          <strong>WebYönetim</strong>
          <span>Adım {step} / 4</span>
        </div>
        {children}
      </div>
    </main>
  );
}
